import React, { useEffect, useState } from "react";
import { Translate } from "react-localize-redux";
import { Typography } from "@mui/material";
import { ColourChip } from "../mini_components-ts";
import { ButtonContinue } from "../mini_components";
import Modal from "../modal";
import { getClinicalSummaryService, ClinicalSummaryParameter } from "../../../services/clinicalSummary";

/**
 * SHER-263 / SHER-287 — Clinical Summary banner.
 *
 * Surfaces a single Clinical Summary parameter (allergies by default) wherever a
 * clinician makes a drug decision. It READS the server's precomputed summary and
 * DISPLAYS it as a clickable chip. Three states:
 *   - grey  "not documented"      — parameter absent / empty and not reviewed.
 *   - green "no known allergies"  — reviewed with no real value (noKnown).
 *   - red   "Allergies: X, Y"     — one or more distinct values recorded.
 * Clicking opens a modal listing the values.
 *
 * SHER-287: allergies are a CURRENT STATE clinicians reconcile. When an `onUpdate`
 * callback is supplied the modal becomes an EDITING ENTRY POINT — it shows an
 * "Update allergies" button that hands the current state (values + noKnown) back
 * to the host so it can open the pre-filled "Update allergies" surface. Without
 * `onUpdate` the banner stays read-only (its original SHER-263 behaviour).
 */
interface ClinicalSummaryBannerProps {
    uuidInvestigation: string;
    uuidPatient: string;
    /** Survey-field type that keys the summary. Defaults to "allergy". */
    parameter?: string;
    /**
     * SHER-287 editing entry point. When provided, the modal offers an "Update
     * allergies" action that calls this with the current state (the pre-fill
     * baseline). When omitted, the banner is read-only.
     */
    onUpdate?: (current: { values: string[]; noKnown: boolean }) => void;
}

const COLOURS = { grey: "#9e9e9e", green: "#4caf50", red: "#f44336" };

const ClinicalSummaryBanner: React.FC<ClinicalSummaryBannerProps> = ({
    uuidInvestigation,
    uuidPatient,
    parameter = "allergy",
    onUpdate,
}) => {
    const [data, setData] = useState<ClinicalSummaryParameter | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        let active = true;
        if (!uuidInvestigation || !uuidPatient) return;
        getClinicalSummaryService(uuidInvestigation, uuidPatient)
            .then((res) => {
                if (!active) return;
                setData(res.clinicalSummary?.[parameter] ?? null);
                setLoaded(true);
            })
            .catch(() => {
                // Banner must never break the host page: degrade to "not documented".
                if (active) setLoaded(true);
            });
        return () => { active = false; };
    }, [uuidInvestigation, uuidPatient, parameter]);

    // Avoid a grey flash before the first fetch resolves.
    if (!loaded) return null;

    const values = data?.values ?? [];
    const hasValues = values.length > 0;
    const noKnown = !!data?.noKnown && !hasValues;

    let colour = COLOURS.grey;
    let label: React.ReactNode = <Translate id={`clinicalSummary.${parameter}.notDocumented`} />;
    if (hasValues) {
        colour = COLOURS.red;
        label = <><Translate id={`clinicalSummary.${parameter}.label`} />: {values.join(", ")}</>;
    } else if (noKnown) {
        colour = COLOURS.green;
        label = <Translate id={`clinicalSummary.${parameter}.noKnown`} />;
    }

    return (
        <>
            <ColourChip
                rgbcolor={colour}
                label={label}
                onClick={() => setShowModal(true)}
                data-testid="clinical-summary-banner"
            />
            {showModal &&
                <Modal
                    open={showModal}
                    closeModal={() => setShowModal(false)}
                    title={<Translate id={`clinicalSummary.${parameter}.label`} />}
                >
                    <>
                    {hasValues
                        ? <ul data-testid="clinical-summary-values">
                            {values.map((v, i) => <li key={i}><Typography>{v}</Typography></li>)}
                        </ul>
                        : noKnown
                            ? <Typography><Translate id={`clinicalSummary.${parameter}.noKnown`} /></Typography>
                            : <Typography><Translate id={`clinicalSummary.${parameter}.notDocumented`} /></Typography>
                    }
                    {onUpdate
                        // SHER-287: editing entry point — hand the current state to the
                        // host, which opens the pre-filled "Update allergies" surface.
                        ? <ButtonContinue
                            data-testid="clinical-summary-update"
                            onClick={() => {
                                setShowModal(false);
                                onUpdate({ values, noKnown });
                            }}
                          >
                            <Translate id={`clinicalSummary.${parameter}.update.title`} />
                          </ButtonContinue>
                        : <Typography variant="caption" color="textSecondary">
                            <Translate id="clinicalSummary.modal.editHint" />
                          </Typography>
                    }
                    </>
                </Modal>
            }
        </>
    );
};

export default ClinicalSummaryBanner;
